import { emailCardsOnYou } from '@reos/today-metrics';
import type { MailCardDto, MailboxViewDto } from './mailbox-api.js';

/**
 * Krok AS1 bod 5 — co karta „Nové z e-mailu" nabídne (čistá logika, bez
 * Reactu a bez textů; popisky dodá obrazovka z katalogu).
 *
 * ZÁKAZY ze zadání (krok 0), které tu drží TVAR dat, ne slib:
 *  - žádná akce „Odpovědět" ani „Návrh odpovědi" (AS3) — typ ji nezná,
 *  - u platby žádné „Označit zaplaceno" — primární akce jen OTEVŘE fakturu,
 *  - u nespárovaného e-mailu (víc kandidátů) žádný tip — „Vyber zakázku".
 *
 * ROZHODNUTÍ AS1-DOC (PŘEKONÁNO krokem PROP5 bodem 5): „Uložit k zakázce"
 * tehdy chybělo, protože schránka obsah příloh neukládá a bezpečný příjem
 * příloh z e-mailu v REOS nebyl. Od PROP5 existuje: server si přílohu dotáhne
 * ze schránky (jen ke čtení), prožene ji antivirem a uloží jako dokument
 * zakázky (`saveMailAttachment`). PRIMÁRNÍ akce karty dokumentu se nemění —
 * zůstává „Otevřít zakázku"; ukládání příloh je akce U PŘÍLOHY, protože jich
 * zpráva nese víc a makléř vybírá, které k zakázce patří.
 */
export type MailPrimaryAction =
  | { readonly kind: 'create_demand' }
  | { readonly kind: 'open_case'; readonly caseId: string }
  | { readonly kind: 'verify_payment'; readonly invoiceId: string | null; readonly caseId: string | null }
  | { readonly kind: 'choose_case'; readonly candidates: readonly string[] }
  | { readonly kind: 'done' };

export function mailPrimaryAction(card: MailCardDto): MailPrimaryAction {
  if (card.chooseCase) return { kind: 'choose_case', candidates: card.candidates };
  switch (card.kind) {
    case 'poptavka':
      return { kind: 'create_demand' };
    case 'platba':
      return { kind: 'verify_payment', invoiceId: card.invoiceId, caseId: card.caseId };
    case 'dokument':
      return card.caseId !== null ? { kind: 'open_case', caseId: card.caseId } : { kind: 'done' };
    default:
      return card.caseId !== null ? { kind: 'open_case', caseId: card.caseId } : { kind: 'done' };
  }
}

/**
 * Krok P1EXP2 bod 3 — ODPOVĚĎ JEDNÍM KLIKEM ZE ŠABLONY.
 *
 * Šablona je jen PŘEDPIS TEXTU: vloží se do odpovědi, která odejde ze
 * schránky makléře stejnou cestou jako napsaná odpověď (MAIL1,
 * `POST /v1/me/mailbox/cards/:id/reply`) — žádná druhá cesta na SMTP.
 *
 * Co se nabízí, rozhoduje DRUH karty: u dotazu zájemce (poptávka z portálu
 * i přímý dotaz) má smysl prohlídka a „nabídka je aktuální"; u ostatních
 * karet jen „ozvu se". Texty jsou v katalogu @reos/i18n
 * (`webAdmin.mailbox.replyTemplate.*`), tady jsou jen KLÍČE — jinak by
 * `en` nemělo paritu (CLAUDE.md §1.5).
 */
export const MAIL_REPLY_TEMPLATES = ['viewing', 'available', 'callback'] as const;
export type MailReplyTemplate = (typeof MAIL_REPLY_TEMPLATES)[number];

export function mailReplyTemplates(card: MailCardDto): readonly MailReplyTemplate[] {
  return card.kind === 'poptavka' ? MAIL_REPLY_TEMPLATES : ['callback'];
}

/** Karty sloupce — TÁŽ funkce jako Dnešek „Na tobě" a MCP (`@reos/today-metrics`), nejnovější nahoře. */
export function mailColumnCards(cards: readonly MailCardDto[]): readonly MailCardDto[] {
  return [...emailCardsOnYou(cards)].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
}

/** Pás nad tabulí: „Schránka <adresa> · připojeno · N roztříděno: …". `null` = schránka není. */
export interface MailboxStripModel {
  readonly address: string;
  readonly state: 'connected' | 'error' | 'demo';
  readonly today: number;
  readonly needsYou: number;
  readonly matched: number;
  readonly noAction: number;
  /** Čas poslední kontroly (ISO) nebo `null`, když ještě neproběhla. */
  readonly checkedAt: string | null;
}

export function mailboxStrip(view: MailboxViewDto | null, openCards: number): MailboxStripModel | null {
  if (view === null || !view.connected || view.mailbox === null) return null;
  const summary = view.summary ?? { today: 0, needsYou: 0, matched: 0, noAction: 0 };
  return {
    address: view.mailbox.address,
    state: view.mailbox.status,
    today: summary.today,
    // „X potřebují tebe" = otevřené karty ve sloupci, ne souhrn ze serveru —
    // po kliknutí „Vyřízeno" musí číslo v pásu klesnout hned, jako sloupec.
    needsYou: openCards,
    matched: summary.matched,
    noAction: summary.noAction,
    checkedAt: view.mailbox.lastCheckedAt,
  };
}

/**
 * Text e-mailu pro náhled karty: první řádky, bez prázdných řádků, nejvýš
 * `max` znaků. Celý e-mail je ve schránce makléře, ne v REOS.
 */
export function mailExcerpt(text: string, max = 180): string {
  const flat = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .join(' ');
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`;
}
